import { useEffect } from 'react';
import { Form, Input, Select, Switch, App } from 'antd';
import { trpc } from '../../../lib/api';
import { withDevModalFilePath } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/companies/CompanyUserModal.tsx');

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  department?: string | null;
  position?: string | null;
  isActive: boolean;
}

interface CompanyUserModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  companyId: string;
  companyName: string;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateFormValues {
  email: string;
  name: string;
  password: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
}

interface EditFormValues {
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  isActive: boolean;
}

export const CompanyUserModal = ({
  open,
  mode,
  companyId,
  companyName,
  user,
  onClose,
  onSuccess,
}: CompanyUserModalProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const createUserMutation = trpc.admin.createUser.useMutation({
    onSuccess: () => {
      message.success('ユーザーを作成しました');
      onSuccess();
    },
    onError: (error) => {
      message.error(error.message || 'ユーザーの作成に失敗しました');
    },
  });

  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      message.success('ユーザー情報を更新しました');
      onSuccess();
    },
    onError: (error) => {
      message.error(error.message || 'ユーザー情報の更新に失敗しました');
    },
  });

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && user) {
        form.setFieldsValue({
          name: user.name,
          email: user.email,
          role: user.role,
          isActive: user.isActive,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          role: 'user',
          isActive: true,
        });
      }
    }
  }, [open, mode, user, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'create') {
        const createValues = values as CreateFormValues;
        await createUserMutation.mutateAsync({
          companyId,
          email: createValues.email,
          name: createValues.name,
          password: createValues.password,
          isActive: true,
        });
      } else if (user) {
        const editValues = values as EditFormValues;
        await updateUserMutation.mutateAsync({
          id: user.id,
          name: editValues.name,
          email: editValues.email,
          role: editValues.role,
          isActive: editValues.isActive,
        });
      }
    } catch {
      // Validation error - do nothing
    }
  };

  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;

  return (
    <Modal
      title={mode === 'create' ? `ユーザーを追加 - ${companyName}` : 'ユーザー情報を編集'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={mode === 'create' ? '作成' : '保存'}
      cancelText="キャンセル"
      confirmLoading={isLoading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          role: 'user',
          isActive: true,
        }}
      >
        <Form.Item
          name="email"
          label="メールアドレス"
          rules={[
            { required: true, message: 'メールアドレスを入力してください' },
            { type: 'email', message: '有効なメールアドレスを入力してください' },
          ]}
        >
          <Input placeholder="user@example.com" />
        </Form.Item>

        <Form.Item
          name="name"
          label="名前"
          rules={[{ required: true, message: '名前を入力してください' }]}
        >
          <Input placeholder="山田 太郎" />
        </Form.Item>

        {mode === 'create' && (
          <Form.Item
            name="password"
            label="初期パスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上で入力してください' },
            ]}
          >
            <Input.Password placeholder="8文字以上" />
          </Form.Item>
        )}

        {mode === 'edit' && (
          <Form.Item
            name="role"
            label="ロール"
            rules={[{ required: true, message: 'ロールを選択してください' }]}
          >
            <Select
              options={[
                { value: 'admin', label: '管理者 - 全ての操作が可能' },
                { value: 'manager', label: 'マネージャー - ユーザー管理を含む操作が可能' },
                { value: 'user', label: 'ユーザー - 一般的な操作が可能' },
                { value: 'viewer', label: '閲覧者 - 閲覧のみ' },
              ]}
            />
          </Form.Item>
        )}

        {mode === 'edit' && (
          <Form.Item name="isActive" label="状態" valuePropName="checked">
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
